/**
 * HostingRig Component - Redesigned Version
 *
 * Unified design system with consistent:
 * - Input heights (44px)
 * - Border radius (8px)
 * - Spacing (16px)
 * - Button sizes
 */

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { useToast } from '../Toast';
import type { FluxNode } from '../../types/fluxNode.types';
import { fluxNodeAPI } from '../../services/fluxNodeApi';
import type { ExtendedNetworkStats } from '../../services/fluxNodeApi';
import type { DatavexExpirationMap, DatavexExpirationInfo } from '../../services/datavexApi';
import { supabase } from '../../lib/supabase/client';

// ===== DESIGN SYSTEM CONSTANTS =====
const DS = {
  // Sizes
  inputHeight: '44px',
  buttonHeight: '44px',
  smallButtonHeight: '36px',

  // Border radius
  radius: {
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
  },

  // Spacing
  gap: {
    xs: '6px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
  },

  // Font sizes
  fontSize: {
    xs: '10px',
    sm: '12px',
    md: '13px',
    lg: '14px',
    xl: '16px',
  },

  // Colors
  colors: {
    inputBg: 'rgba(0, 0, 0, 0.35)',
    inputBorder: 'rgba(255, 255, 255, 0.12)',
    inputBorderFocus: 'rgba(139, 92, 246, 0.5)',
    cardBg: 'rgba(0, 0, 0, 0.25)',
    sectionBg: 'rgba(0, 0, 0, 0.2)',
  },
};

// Unified input style
const inputStyle = {
  height: DS.inputHeight,
  padding: '0 14px',
  background: DS.colors.inputBg,
  border: `1px solid ${DS.colors.inputBorder}`,
  borderRadius: DS.radius.md,
  color: 'var(--text)',
  fontSize: DS.fontSize.sm,
  width: '100%',
  boxSizing: 'border-box' as const,
};

// Unified button style
const buttonStyle = {
  height: DS.buttonHeight,
  padding: '0 20px',
  borderRadius: DS.radius.md,
  fontSize: DS.fontSize.md,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: DS.gap.sm,
  border: 'none',
  transition: 'all 0.15s ease',
};

// Hosting prices (EUR/month)
const HOSTING_PRICES = {
  cumulus: 7.99,
  nimbus: 19.99,
  stratus: 34.99,
};

// Node tier specifications
const TIER_SPECS = {
  cumulus: {
    collateral: 1000,
    cores: 2,
    ram: 8,
    ssd: 220,
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)',
  },
  nimbus: {
    collateral: 12500,
    cores: 4,
    ram: 32,
    ssd: 440,
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
  },
  stratus: {
    collateral: 40000,
    cores: 8,
    ram: 64,
    ssd: 880,
    color: '#ec4899',
    gradient: 'linear-gradient(135deg, #ec4899, #db2777)',
  },
};

interface NodeEntry {
  id: string;
  tier: 'cumulus' | 'nimbus' | 'stratus';
  txhash: string;
  outidx: string;
  paymentAddress: string;
}

interface WalletCredentials {
  walletAddress: string;
  zelId: string;
  identityKey: string;
}

interface HostingRigProps {
  wallets: string[];
  nodes: FluxNode[];
  fluxPrice: number;
  extendedStats?: ExtendedNetworkStats;
  datavexHostedIps?: Set<string> | null;
  datavexExpirations?: DatavexExpirationMap | null;
}

export function HostingRig({ wallets, nodes, fluxPrice, extendedStats, datavexHostedIps, datavexExpirations }: HostingRigProps) {
  const { t } = useTranslation();
  const toast = useToast();

  // State
  const [inputMode, setInputMode] = useState<'manual' | 'import'>(wallets.length > 0 ? 'import' : 'manual');
  const [hasUserSwitchedMode, setHasUserSwitchedMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());
  const [showManagedNodes, setShowManagedNodes] = useState(true);
  const [email, setEmail] = useState('');
  const [duration, setDuration] = useState<1 | 3 | 6 | 12>(1);
  const [walletCredentials, setWalletCredentials] = useState<Record<string, WalletCredentials>>({});
  const [manualZelId, setManualZelId] = useState('');
  const [manualIdentityKey, setManualIdentityKey] = useState('');
  const [nodeEntries, setNodeEntries] = useState<NodeEntry[]>([
    { id: '1', tier: 'cumulus', txhash: '', outidx: '0', paymentAddress: '' }
  ]);

  // Auto-switch to import mode when wallets become available
  useEffect(() => {
    if (wallets.length > 0 && !hasUserSwitchedMode && inputMode === 'manual') {
      setInputMode('import');
    }
  }, [wallets.length, hasUserSwitchedMode, inputMode]);

  // Auto-fill email from logged-in user
  useEffect(() => {
    async function fetchUserEmail() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email && !email) {
          setEmail(user.email);
        }
      } catch (err) {
        console.error('[HostingRig] Error fetching user email:', err);
      }
    }
    fetchUserEmail();
  }, []);

  // Node entry helpers
  const addNodeEntry = () => {
    setNodeEntries(prev => [...prev, { id: String(Date.now()), tier: 'cumulus', txhash: '', outidx: '0', paymentAddress: '' }]);
  };

  const removeNodeEntry = (id: string) => {
    if (nodeEntries.length > 1) {
      setNodeEntries(prev => prev.filter(n => n.id !== id));
    }
  };

  const updateNodeEntry = (id: string, field: keyof NodeEntry, value: string) => {
    setNodeEntries(prev => prev.map(n => n.id === id ? { ...n, [field]: value } : n));
  };

  // Calculate rewards per tier
  const tierRewards = useMemo(() => {
    if (!extendedStats) {
      return {
        cumulus: { daily: 3.2, monthly: 96 },
        nimbus: { daily: 9.5, monthly: 285 },
        stratus: { daily: 19, monthly: 570 },
      };
    }
    const cumulusRewards = fluxNodeAPI.calculateRealRewards('CUMULUS', extendedStats.cumulusCount, extendedStats.fluxPrice);
    const nimbusRewards = fluxNodeAPI.calculateRealRewards('NIMBUS', extendedStats.nimbusCount, extendedStats.fluxPrice);
    const stratusRewards = fluxNodeAPI.calculateRealRewards('STRATUS', extendedStats.stratusCount, extendedStats.fluxPrice);
    return {
      cumulus: { daily: cumulusRewards.daily, monthly: cumulusRewards.monthly },
      nimbus: { daily: nimbusRewards.daily, monthly: nimbusRewards.monthly },
      stratus: { daily: stratusRewards.daily, monthly: stratusRewards.monthly },
    };
  }, [extendedStats]);

  const getDailyReward = (tier: 'cumulus' | 'nimbus' | 'stratus'): number => tierRewards[tier].daily;
  const getMonthlyReward = (tier: 'cumulus' | 'nimbus' | 'stratus'): number => tierRewards[tier].monthly;

  // Get Datavex-hosted nodes
  const datavexHostedNodes = useMemo(() => {
    if (!datavexExpirations) return [];
    const hostedNodes: Array<{ node: FluxNode; expiration: DatavexExpirationInfo }> = [];
    nodes.forEach(node => {
      const nodeIp = node.ip?.split(':')[0] || '';
      const expiration = datavexExpirations.get(nodeIp);
      if (expiration) {
        hostedNodes.push({ node, expiration });
      }
    });
    return hostedNodes;
  }, [nodes, datavexExpirations]);

  // Group Datavex-hosted nodes by wallet
  const datavexHostedByWallet = useMemo(() => {
    const groups: Record<string, Array<{ node: FluxNode; expiration: DatavexExpirationInfo }>> = {};
    datavexHostedNodes.forEach(item => {
      const wallet = item.node.payment_address;
      if (!groups[wallet]) groups[wallet] = [];
      groups[wallet].push(item);
    });
    return groups;
  }, [datavexHostedNodes]);

  const hostedWallets = useMemo(() => Object.keys(datavexHostedByWallet), [datavexHostedByWallet]);

  // Filter available nodes
  const availableNodes = useMemo(() => {
    return nodes.filter(node => {
      const nodeIp = node.ip?.split(':')[0] || '';
      return !(datavexHostedIps?.has(nodeIp) ?? false);
    });
  }, [nodes, datavexHostedIps]);

  // Group nodes by wallet
  const nodesByWallet = useMemo(() => {
    const groups: Record<string, FluxNode[]> = {};
    availableNodes.forEach(node => {
      const wallet = node.payment_address;
      if (!groups[wallet]) groups[wallet] = [];
      groups[wallet].push(node);
    });
    return groups;
  }, [availableNodes]);

  const uniqueWallets = useMemo(() => Object.keys(nodesByWallet), [nodesByWallet]);

  // Initialize wallet credentials
  useEffect(() => {
    const newCredentials: Record<string, WalletCredentials> = { ...walletCredentials };
    uniqueWallets.forEach(wallet => {
      if (!newCredentials[wallet]) {
        newCredentials[wallet] = { walletAddress: wallet, zelId: '', identityKey: '' };
      }
    });
    if (Object.keys(newCredentials).length !== Object.keys(walletCredentials).length) {
      setWalletCredentials(newCredentials);
    }
  }, [uniqueWallets]);

  const updateWalletCredential = (wallet: string, field: 'zelId' | 'identityKey', value: string) => {
    setWalletCredentials(prev => ({
      ...prev,
      [wallet]: { ...prev[wallet], walletAddress: wallet, [field]: value },
    }));
  };

  // Stats
  const activeHostedCount = datavexHostedNodes.length;
  const expiringSoonCount = datavexHostedNodes.filter(n => n.expiration.isExpiringSoon).length;
  const monthlyTotal = datavexHostedNodes.reduce((sum, n) => {
    const tierLower = n.node.tier.toLowerCase() as keyof typeof HOSTING_PRICES;
    return sum + (HOSTING_PRICES[tierLower] || 0);
  }, 0);

  // Wallets with selected nodes
  const walletsWithSelectedNodes = useMemo(() => {
    const walletSet = new Set<string>();
    availableNodes.forEach(node => {
      if (selectedNodes.has(`${node.txhash}:${node.outidx}`)) {
        walletSet.add(node.payment_address);
      }
    });
    return Array.from(walletSet);
  }, [availableNodes, selectedNodes]);

  // Validation
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const areCredentialsValid = useMemo(() => {
    if (inputMode === 'manual') {
      return manualZelId.trim().length >= 10 && manualIdentityKey.trim().length >= 5;
    } else {
      return walletsWithSelectedNodes.every(wallet => {
        const creds = walletCredentials[wallet];
        return creds && creds.zelId.trim().length >= 10 && creds.identityKey.trim().length >= 5;
      });
    }
  }, [inputMode, manualZelId, manualIdentityKey, walletsWithSelectedNodes, walletCredentials]);
  const isFormValid = isEmailValid && areCredentialsValid;

  // Submit handler
  const handleSubmit = async () => {
    if (!isFormValid) {
      toast(t('fluxNode.hosting.errors.invalidForm', 'Veuillez remplir tous les champs requis'), 'error');
      return;
    }

    const nodesToSubmit = inputMode === 'manual'
      ? nodeEntries.map(entry => ({
          tier: entry.tier,
          txhash: entry.txhash,
          outidx: entry.outidx,
          paymentAddress: entry.paymentAddress,
        }))
      : availableNodes
          .filter(n => selectedNodes.has(`${n.txhash}:${n.outidx}`))
          .map(node => ({
            tier: node.tier.toLowerCase() as 'cumulus' | 'nimbus' | 'stratus',
            txhash: node.txhash,
            outidx: node.outidx,
            paymentAddress: node.payment_address,
            ip: node.ip,
          }));

    if (nodesToSubmit.length === 0) {
      toast(t('fluxNode.hosting.selectNodes', 'Sélectionnez des nodes'), 'warning');
      return;
    }

    if (inputMode === 'manual') {
      for (let i = 0; i < nodeEntries.length; i++) {
        const entry = nodeEntries[i];
        if (!entry.txhash || entry.txhash.length < 64) {
          toast(`Node #${i + 1}: ${t('fluxNode.hosting.toasts.invalidTxHash')}`, 'error');
          return;
        }
        if (!entry.paymentAddress || !entry.paymentAddress.startsWith('t')) {
          toast(`Node #${i + 1}: ${t('fluxNode.hosting.toasts.invalidPaymentAddress')}`, 'error');
          return;
        }
      }
    }

    setIsSubmitting(true);

    try {
      const credentialsArray = inputMode === 'manual'
        ? [{ walletAddress: nodeEntries[0]?.paymentAddress || '', zelId: manualZelId, identityKey: manualIdentityKey }]
        : walletsWithSelectedNodes.map(wallet => ({
            walletAddress: wallet,
            zelId: walletCredentials[wallet]?.zelId || '',
            identityKey: walletCredentials[wallet]?.identityKey || '',
          }));

      const checkoutResponse = await fetch('/api/hosting-rig/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerEmail: email,
          duration,
          mode: inputMode,
          language: navigator.language?.startsWith('fr') ? 'fr' : 'en',
          walletCredentials: credentialsArray,
          nodes: nodesToSubmit,
        }),
      });

      if (!checkoutResponse.ok) {
        const errorData = await checkoutResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await checkoutResponse.json();
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast(t('fluxNode.hosting.errors.submitFailed', 'Erreur lors de l\'envoi'), 'error');
      setIsSubmitting(false);
    }
  };

  const toggleNodeSelection = (nodeId: string) => {
    setSelectedNodes(prev => {
      const next = new Set(prev);
      // Toggle membership — explicit if/else instead of a discarded ternary
      // expression (no-unused-expressions). Same behavior as before.
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  // Summary calculations
  const summaryNodes = inputMode === 'manual'
    ? nodeEntries.map(n => ({ tier: n.tier, price: HOSTING_PRICES[n.tier] }))
    : availableNodes.filter(n => selectedNodes.has(`${n.txhash}:${n.outidx}`)).map(n => ({
        tier: n.tier.toLowerCase() as 'cumulus' | 'nimbus' | 'stratus',
        price: HOSTING_PRICES[n.tier.toLowerCase() as keyof typeof HOSTING_PRICES]
      }));
  const summaryTotal = summaryNodes.reduce((sum, n) => sum + n.price, 0);
  const summaryRewards = summaryNodes.reduce((sum, n) => sum + getMonthlyReward(n.tier), 0);

  return (
    <div className="card">
      {/* ===== HEADER + PRICING ===== */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(99, 102, 241, 0.06))',
        borderRadius: DS.radius.lg,
        padding: DS.gap.xl,
        marginBottom: DS.gap.xl,
      }}>
        {/* Title + Stats */}
        <div className="hosting-header-stats" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: DS.gap.lg, marginBottom: DS.gap.lg }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: DS.gap.md }}>
            <Icons.Server size={24} style={{ color: '#8b5cf6' }} />
            <h2 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>{t('fluxNode.hosting.title')}</h2>
          </div>
          <div className="hosting-stats-row" style={{ display: 'flex', gap: DS.gap.md }}>
            {[
              { value: activeHostedCount, label: t('fluxNode.hosting.stats.active'), color: '#10b981' },
              { value: expiringSoonCount, label: t('fluxNode.hosting.stats.expiringSoon', 'Exp. bientôt'), color: '#f59e0b' },
              { value: `${monthlyTotal.toFixed(2)}€`, label: t('fluxNode.hosting.stats.perMonth'), color: '#8b5cf6' },
            ].map((stat, i) => (
              <div key={i} style={{
                textAlign: 'center',
                padding: '10px 16px',
                background: 'var(--card-bg)',
                borderRadius: DS.radius.md,
                minWidth: '80px',
              }}>
                <div style={{ fontSize: '20px', fontWeight: 700, color: stat.color }}>{stat.value}</div>
                <div style={{ fontSize: DS.fontSize.xs, color: 'var(--muted)', marginTop: '2px' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="hosting-pricing-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: DS.gap.md }}>
          {(['cumulus', 'nimbus', 'stratus'] as const).map(tier => {
            const specs = TIER_SPECS[tier];
            const price = HOSTING_PRICES[tier];
            const dailyReward = getDailyReward(tier);
            const monthlyRewardEur = dailyReward * 30 * fluxPrice;
            const roi = monthlyRewardEur > 0 ? ((monthlyRewardEur - price) / price * 100) : 0;

            return (
              <div key={tier} className="hosting-pricing-card" style={{
                background: 'var(--card-bg)',
                borderRadius: DS.radius.md,
                padding: DS.gap.lg,
                border: `1px solid ${specs.color}20`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: DS.gap.sm }}>
                  <span style={{
                    background: specs.gradient,
                    color: 'white',
                    padding: '4px 10px',
                    borderRadius: DS.radius.sm,
                    fontSize: DS.fontSize.xs,
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                  }}>
                    {tier.toUpperCase()}
                  </span>
                  <span style={{ fontSize: '18px', fontWeight: 700, color: specs.color }}>{price}€</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: DS.fontSize.xs, color: 'var(--muted)' }}>
                  <span>{specs.collateral.toLocaleString()} FLUX</span>
                  <span>{specs.cores}C/{specs.ram}GB/{specs.ssd}GB</span>
                </div>
                {fluxPrice > 0 && (
                  <div style={{
                    marginTop: DS.gap.sm,
                    padding: '6px 8px',
                    background: roi > 0 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                    borderRadius: DS.radius.sm,
                    fontSize: DS.fontSize.xs,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}>
                    <span>~{(dailyReward * 30).toFixed(0)} FLUX/m</span>
                    <span style={{ color: roi > 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                      {roi > 0 ? '+' : ''}{roi.toFixed(0)}% ROI
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== MAIN CONTENT ===== */}
      <div className="hosting-main-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: DS.gap.xl, marginBottom: DS.gap.xl }}>
        {/* Left: Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: DS.gap.lg }}>
          {/* Mode Toggle */}
          <div className="hosting-mode-toggle" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: DS.gap.sm }}>
            {(['manual', 'import'] as const).map(mode => (
              <button
                key={mode}
                onClick={() => { setInputMode(mode); setHasUserSwitchedMode(true); }}
                style={{
                  ...buttonStyle,
                  background: inputMode === mode ? 'linear-gradient(135deg, #8b5cf6, #6366f1)' : DS.colors.cardBg,
                  color: inputMode === mode ? 'white' : 'var(--text)',
                  border: inputMode === mode ? 'none' : `1px solid ${DS.colors.inputBorder}`,
                }}
              >
                {mode === 'manual' ? <Icons.Edit size={18} /> : <Icons.Download size={18} />}
                {mode === 'manual' ? t('fluxNode.hosting.modes.manual') : `${t('fluxNode.hosting.modes.import')} (${availableNodes.length})`}
              </button>
            ))}
          </div>

          {/* Email & Duration */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 140px',
            gap: DS.gap.md,
            padding: DS.gap.lg,
            background: DS.colors.sectionBg,
            borderRadius: DS.radius.lg,
            border: `1px solid ${DS.colors.inputBorder}`,
          }}>
            <div>
              <label style={{ display: 'block', fontSize: DS.fontSize.sm, fontWeight: 600, marginBottom: DS.gap.sm, color: 'var(--text)' }}>
                {t('fluxNode.hosting.form.email')} *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: DS.fontSize.sm, fontWeight: 600, marginBottom: DS.gap.sm, color: 'var(--text)' }}>
                {t('fluxNode.hosting.form.duration')}
              </label>
              <select
                aria-label={t('fluxNode.hosting.form.duration')}
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value) as 1 | 3 | 6 | 12)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value={1}>{t('fluxNode.hosting.form.month1')}</option>
                <option value={3}>{t('fluxNode.hosting.form.month3')}</option>
                <option value={6}>{t('fluxNode.hosting.form.month6')}</option>
                <option value={12}>{t('fluxNode.hosting.form.month12')}</option>
              </select>
            </div>
          </div>

          {/* MANUAL MODE */}
          {inputMode === 'manual' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: DS.gap.lg }}>
              {/* Wallet Credentials */}
              <div style={{
                padding: DS.gap.lg,
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08), rgba(99, 102, 241, 0.04))',
                borderRadius: DS.radius.lg,
                border: '1px solid rgba(139, 92, 246, 0.2)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: DS.gap.sm, marginBottom: DS.gap.md }}>
                  <Icons.Key size={18} style={{ color: '#8b5cf6' }} />
                  <span style={{ fontSize: DS.fontSize.lg, fontWeight: 600 }}>
                    {t('fluxNode.hosting.form.walletCredentials', 'Identifiants Wallet')}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: DS.gap.md }}>
                  <div>
                    <label style={{ display: 'block', fontSize: DS.fontSize.sm, fontWeight: 600, marginBottom: DS.gap.sm }}>
                      {t('fluxNode.hosting.form.zelId', 'Zel ID')} *
                    </label>
                    <input
                      type="text"
                      value={manualZelId}
                      onChange={(e) => setManualZelId(e.target.value)}
                      placeholder="1CbErtneaX2QVyUfwU..."
                      style={{ ...inputStyle, fontFamily: 'monospace' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: DS.fontSize.sm, fontWeight: 600, marginBottom: DS.gap.sm }}>
                      {t('fluxNode.hosting.form.identityKey', 'Identity Key')} *
                    </label>
                    <input
                      type="text"
                      value={manualIdentityKey}
                      onChange={(e) => setManualIdentityKey(e.target.value)}
                      placeholder="abc123..."
                      style={{ ...inputStyle, fontFamily: 'monospace' }}
                    />
                  </div>
                </div>
              </div>

              {/* Node Entries */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: DS.gap.md }}>
                  <span style={{ fontSize: DS.fontSize.lg, fontWeight: 600 }}>
                    {t('fluxNode.hosting.nodes.toHost')} ({nodeEntries.length})
                  </span>
                  <button
                    onClick={addNodeEntry}
                    style={{
                      ...buttonStyle,
                      height: DS.smallButtonHeight,
                      padding: '0 14px',
                      background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                      color: 'white',
                      fontSize: DS.fontSize.sm,
                    }}
                  >
                    <Icons.Plus size={16} /> {t('fluxNode.hosting.nodes.add')}
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: DS.gap.md, maxHeight: '350px', overflowY: 'auto' }}>
                  {nodeEntries.map((entry) => {
                    const tierSpecs = TIER_SPECS[entry.tier];
                    return (
                      <div key={entry.id} style={{
                        background: DS.colors.sectionBg,
                        borderRadius: DS.radius.lg,
                        padding: DS.gap.lg,
                        border: `1px solid ${tierSpecs.color}30`,
                      }}>
                        {/* Tier Selection - Unified Height */}
                        <div style={{ display: 'grid', gridTemplateColumns: nodeEntries.length > 1 ? '1fr 1fr 1fr 44px' : '1fr 1fr 1fr', gap: DS.gap.sm, marginBottom: DS.gap.md }}>
                          {(['cumulus', 'nimbus', 'stratus'] as const).map(tier => (
                            <button
                              key={tier}
                              onClick={() => updateNodeEntry(entry.id, 'tier', tier)}
                              style={{
                                height: DS.buttonHeight,
                                background: entry.tier === tier ? TIER_SPECS[tier].gradient : DS.colors.inputBg,
                                color: entry.tier === tier ? 'white' : 'var(--text)',
                                border: entry.tier === tier ? 'none' : `1px solid ${DS.colors.inputBorder}`,
                                borderRadius: DS.radius.md,
                                cursor: 'pointer',
                                fontSize: DS.fontSize.sm,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                              }}
                            >
                              {tier}
                            </button>
                          ))}
                          {nodeEntries.length > 1 && (
                            <button
                              onClick={() => removeNodeEntry(entry.id)}
                              style={{
                                height: DS.buttonHeight,
                                width: DS.buttonHeight,
                                background: 'rgba(239, 68, 68, 0.1)',
                                color: '#ef4444',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                borderRadius: DS.radius.md,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Icons.Trash2 size={18} />
                            </button>
                          )}
                        </div>

                        {/* Transaction Hash + Index */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: DS.gap.sm, marginBottom: DS.gap.sm }}>
                          <input
                            type="text"
                            value={entry.txhash}
                            onChange={(e) => updateNodeEntry(entry.id, 'txhash', e.target.value)}
                            placeholder={t('fluxNode.hosting.nodes.txHashPlaceholder')}
                            style={{ ...inputStyle, fontFamily: 'monospace', fontSize: DS.fontSize.xs }}
                          />
                          <input
                            type="text"
                            value={entry.outidx}
                            onChange={(e) => updateNodeEntry(entry.id, 'outidx', e.target.value)}
                            placeholder="idx"
                            style={{ ...inputStyle, fontFamily: 'monospace', fontSize: DS.fontSize.xs, textAlign: 'center' }}
                          />
                        </div>

                        {/* Payment Address */}
                        <input
                          type="text"
                          value={entry.paymentAddress}
                          onChange={(e) => updateNodeEntry(entry.id, 'paymentAddress', e.target.value)}
                          placeholder={t('fluxNode.hosting.nodes.paymentAddressPlaceholder')}
                          style={{ ...inputStyle, fontFamily: 'monospace', fontSize: DS.fontSize.xs }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* IMPORT MODE */}
          {inputMode === 'import' && (
            <div>
              {uniqueWallets.length > 0 ? (
                <>
                  {/* Header with select all */}
                  {(() => {
                    const allSelected = availableNodes.length > 0 && availableNodes.every(n => selectedNodes.has(`${n.txhash}:${n.outidx}`));
                    return (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: DS.gap.lg }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: DS.gap.sm }}>
                          <span style={{ fontSize: DS.fontSize.lg, fontWeight: 600 }}>
                            {t('fluxNode.hosting.import.selectNodes')} ({selectedNodes.size}/{availableNodes.length})
                          </span>
                          {datavexHostedNodes.length > 0 && (
                            <span style={{
                              padding: '4px 10px',
                              background: 'rgba(59, 130, 246, 0.15)',
                              color: '#3b82f6',
                              borderRadius: '12px',
                              fontSize: DS.fontSize.xs,
                              fontWeight: 500,
                            }}>
                              {datavexHostedNodes.length} {t('fluxNode.hosting.alreadyHosted', 'déjà hébergé(s)')}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            if (allSelected) setSelectedNodes(new Set());
                            else setSelectedNodes(new Set(availableNodes.map(n => `${n.txhash}:${n.outidx}`)));
                          }}
                          disabled={availableNodes.length === 0}
                          style={{
                            ...buttonStyle,
                            height: DS.smallButtonHeight,
                            padding: '0 14px',
                            background: allSelected ? 'var(--accent)' : DS.colors.cardBg,
                            color: allSelected ? 'white' : 'var(--text)',
                            border: `1px solid ${DS.colors.inputBorder}`,
                            fontSize: DS.fontSize.sm,
                            opacity: availableNodes.length === 0 ? 0.5 : 1,
                          }}
                        >
                          {allSelected ? t('fluxNode.hosting.import.deselectAll') : t('fluxNode.hosting.import.selectAll')}
                        </button>
                      </div>
                    );
                  })()}

                  {/* Wallet Groups */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: DS.gap.lg, maxHeight: '500px', overflowY: 'auto' }}>
                    {uniqueWallets.map(walletAddress => {
                      const walletNodes = nodesByWallet[walletAddress] || [];
                      const selectedInWallet = walletNodes.filter(n => selectedNodes.has(`${n.txhash}:${n.outidx}`)).length;
                      const hasSelectedNodes = selectedInWallet > 0;
                      const creds = walletCredentials[walletAddress] || { zelId: '', identityKey: '' };
                      const isCredentialsComplete = creds.zelId.length >= 10 && creds.identityKey.length >= 5;
                      const allInWalletSelected = walletNodes.length > 0 && walletNodes.every(n => selectedNodes.has(`${n.txhash}:${n.outidx}`));

                      return (
                        <div key={walletAddress} style={{
                          background: hasSelectedNodes ? 'rgba(139, 92, 246, 0.06)' : DS.colors.sectionBg,
                          borderRadius: DS.radius.lg,
                          border: hasSelectedNodes ? '1px solid rgba(139, 92, 246, 0.25)' : `1px solid ${DS.colors.inputBorder}`,
                          overflow: 'hidden',
                        }}>
                          {/* Wallet Header */}
                          <div style={{
                            padding: DS.gap.lg,
                            background: 'rgba(0, 0, 0, 0.15)',
                            borderBottom: `1px solid ${DS.colors.inputBorder}`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: DS.gap.md }}>
                              <Icons.Wallet size={18} style={{ color: '#8b5cf6' }} />
                              <div>
                                <div style={{ fontSize: DS.fontSize.sm, fontFamily: 'monospace', fontWeight: 600 }}>
                                  {walletAddress.substring(0, 12)}...{walletAddress.substring(walletAddress.length - 8)}
                                </div>
                                <div style={{ fontSize: DS.fontSize.xs, color: 'var(--muted)' }}>
                                  {walletNodes.length} {walletNodes.length > 1 ? 'nodes' : 'node'}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                const ids = walletNodes.map(n => `${n.txhash}:${n.outidx}`);
                                setSelectedNodes(prev => {
                                  const next = new Set(prev);
                                  if (allInWalletSelected) ids.forEach(id => next.delete(id));
                                  else ids.forEach(id => next.add(id));
                                  return next;
                                });
                              }}
                              style={{
                                ...buttonStyle,
                                height: DS.smallButtonHeight,
                                padding: '0 12px',
                                background: allInWalletSelected ? 'linear-gradient(135deg, #8b5cf6, #6366f1)' : DS.colors.inputBg,
                                color: 'white',
                                fontSize: DS.fontSize.xs,
                              }}
                            >
                              {allInWalletSelected ? t('fluxNode.hosting.import.deselectAll') : t('fluxNode.hosting.import.selectAll')}
                            </button>
                          </div>

                          {/* Wallet Credentials */}
                          {hasSelectedNodes && (
                            <div style={{
                              padding: DS.gap.lg,
                              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08), rgba(99, 102, 241, 0.04))',
                              borderBottom: `1px solid ${DS.colors.inputBorder}`,
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: DS.gap.sm, marginBottom: DS.gap.md }}>
                                <Icons.Key size={16} style={{ color: '#8b5cf6' }} />
                                <span style={{ fontSize: DS.fontSize.sm, fontWeight: 600 }}>
                                  {t('fluxNode.hosting.form.walletCredentials')}
                                </span>
                                {isCredentialsComplete && (
                                  <span style={{
                                    padding: '3px 8px',
                                    background: 'rgba(16, 185, 129, 0.15)',
                                    color: '#10b981',
                                    borderRadius: DS.radius.sm,
                                    fontSize: DS.fontSize.xs,
                                    fontWeight: 600,
                                  }}>
                                    ✓ {t('fluxNode.hosting.form.complete', 'Complet')}
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: DS.gap.md }}>
                                <div>
                                  <label style={{ display: 'block', fontSize: DS.fontSize.xs, fontWeight: 600, marginBottom: DS.gap.xs, color: 'rgba(255,255,255,0.7)' }}>
                                    {t('fluxNode.hosting.form.zelId')} *
                                  </label>
                                  <input
                                    type="text"
                                    value={creds.zelId}
                                    onChange={(e) => updateWalletCredential(walletAddress, 'zelId', e.target.value)}
                                    placeholder="1CbErtneaX2QVyUfwU..."
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                      ...inputStyle,
                                      fontFamily: 'monospace',
                                      fontSize: DS.fontSize.xs,
                                      borderColor: creds.zelId.length >= 10 ? 'rgba(16, 185, 129, 0.4)' : DS.colors.inputBorder,
                                    }}
                                  />
                                </div>
                                <div>
                                  <label style={{ display: 'block', fontSize: DS.fontSize.xs, fontWeight: 600, marginBottom: DS.gap.xs, color: 'rgba(255,255,255,0.7)' }}>
                                    {t('fluxNode.hosting.form.identityKey')} *
                                  </label>
                                  <input
                                    type="text"
                                    value={creds.identityKey}
                                    onChange={(e) => updateWalletCredential(walletAddress, 'identityKey', e.target.value)}
                                    placeholder="abc123..."
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                      ...inputStyle,
                                      fontFamily: 'monospace',
                                      fontSize: DS.fontSize.xs,
                                      borderColor: creds.identityKey.length >= 5 ? 'rgba(16, 185, 129, 0.4)' : DS.colors.inputBorder,
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Nodes List */}
                          <div style={{ padding: DS.gap.md, maxHeight: '350px', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: DS.gap.sm }}>
                              {walletNodes.map(node => {
                                const nodeId = `${node.txhash}:${node.outidx}`;
                                const isSelected = selectedNodes.has(nodeId);
                                const tierLower = node.tier.toLowerCase() as keyof typeof TIER_SPECS;
                                const specs = TIER_SPECS[tierLower];

                                return (
                                  <div
                                    key={nodeId}
                                    onClick={() => toggleNodeSelection(nodeId)}
                                    style={{
                                      height: '56px',
                                      padding: '0 14px',
                                      background: isSelected ? `${specs.color}12` : DS.colors.inputBg,
                                      border: `1px solid ${isSelected ? specs.color + '40' : 'transparent'}`,
                                      borderRadius: DS.radius.md,
                                      cursor: 'pointer',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      transition: 'all 0.15s',
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: DS.gap.md }}>
                                      <div style={{
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '4px',
                                        background: isSelected ? specs.color : 'transparent',
                                        border: `2px solid ${specs.color}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}>
                                        {isSelected && <Icons.Check size={12} style={{ color: 'white' }} />}
                                      </div>
                                      <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: DS.gap.sm }}>
                                          <span style={{
                                            background: specs.gradient,
                                            color: 'white',
                                            padding: '3px 8px',
                                            borderRadius: '4px',
                                            fontSize: DS.fontSize.xs,
                                            fontWeight: 700,
                                          }}>
                                            {node.tier}
                                          </span>
                                          {node.ip && <span style={{ fontSize: DS.fontSize.xs, color: 'var(--muted)' }}>{node.ip}</span>}
                                        </div>
                                      </div>
                                    </div>
                                    <span style={{ fontSize: DS.fontSize.lg, fontWeight: 700, color: specs.color }}>
                                      {HOSTING_PRICES[tierLower]}€
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--muted)' }}>
                  <Icons.Server size={48} style={{ marginBottom: DS.gap.lg, opacity: 0.3 }} />
                  <p style={{ fontSize: DS.fontSize.lg, margin: 0 }}>
                    {wallets.length === 0 ? t('fluxNode.hosting.import.addWalletsFirst') : t('fluxNode.hosting.import.allImported')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: Summary */}
        <div className="hosting-summary-panel" style={{
          background: DS.colors.cardBg,
          borderRadius: DS.radius.lg,
          padding: DS.gap.xl,
          height: 'fit-content',
          position: 'sticky',
          top: '20px',
          border: `1px solid ${DS.colors.inputBorder}`,
        }}>
          <h3 style={{ fontSize: DS.fontSize.lg, fontWeight: 600, marginBottom: DS.gap.lg, display: 'flex', alignItems: 'center', gap: DS.gap.sm }}>
            <Icons.FileText size={18} />
            {t('fluxNode.hosting.summary.title')}
          </h3>

          {summaryNodes.length > 0 ? (
            <>
              {/* Tier breakdown */}
              {(['cumulus', 'nimbus', 'stratus'] as const).map(tier => {
                const count = summaryNodes.filter(n => n.tier === tier).length;
                if (count === 0) return null;
                return (
                  <div key={tier} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: '42px',
                    padding: '0 12px',
                    background: `${TIER_SPECS[tier].color}10`,
                    borderRadius: DS.radius.md,
                    marginBottom: DS.gap.sm,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: DS.gap.sm }}>
                      <span style={{
                        background: TIER_SPECS[tier].gradient,
                        color: 'white',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        fontSize: DS.fontSize.xs,
                        fontWeight: 700,
                      }}>
                        {tier.toUpperCase()}
                      </span>
                      <span style={{ fontSize: DS.fontSize.sm }}>×{count}</span>
                    </div>
                    <span style={{ fontWeight: 600, fontSize: DS.fontSize.sm, color: TIER_SPECS[tier].color }}>
                      {(HOSTING_PRICES[tier] * count).toFixed(2)}€
                    </span>
                  </div>
                );
              })}

              {/* Totals */}
              <div style={{ borderTop: `1px solid ${DS.colors.inputBorder}`, paddingTop: DS.gap.lg, marginTop: DS.gap.md }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: DS.fontSize.sm, marginBottom: DS.gap.sm }}>
                  <span style={{ color: 'var(--muted)' }}>{t('fluxNode.hosting.summary.monthly')}</span>
                  <span style={{ fontWeight: 600 }}>{summaryTotal.toFixed(2)}€</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: DS.fontSize.sm, marginBottom: DS.gap.md }}>
                  <span style={{ color: 'var(--muted)' }}>{t('fluxNode.hosting.summary.duration')}</span>
                  <span style={{ fontWeight: 600 }}>{duration} {t('fluxNode.hosting.form.months')}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  height: '48px',
                  padding: '0 14px',
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12), rgba(99, 102, 241, 0.08))',
                  borderRadius: DS.radius.md,
                }}>
                  <span style={{ fontWeight: 600, fontSize: DS.fontSize.sm }}>Total</span>
                  <span style={{ fontWeight: 700, fontSize: '18px', color: '#8b5cf6' }}>
                    {(summaryTotal * duration).toFixed(2)}€
                  </span>
                </div>
              </div>

              {/* Estimated rewards */}
              {fluxPrice > 0 && (
                <div style={{
                  marginTop: DS.gap.md,
                  padding: DS.gap.md,
                  background: 'rgba(16, 185, 129, 0.08)',
                  borderRadius: DS.radius.md,
                  border: '1px solid rgba(16, 185, 129, 0.15)',
                }}>
                  <div style={{ fontSize: DS.fontSize.xs, color: 'var(--muted)', marginBottom: '4px' }}>
                    {t('fluxNode.hosting.summary.estimatedRewards')} {extendedStats && <span style={{ color: '#10b981' }}>● Live</span>}
                  </div>
                  <div style={{ fontSize: DS.fontSize.lg, fontWeight: 600, color: '#10b981' }}>
                    ~{summaryRewards.toFixed(1)} FLUX/mois
                  </div>
                  <div style={{ fontSize: DS.fontSize.xs, color: 'var(--muted)' }}>
                    ~{(summaryRewards * fluxPrice).toFixed(2)}€
                  </div>
                </div>
              )}

              {/* Submit button */}
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !isFormValid || (inputMode === 'import' && selectedNodes.size === 0)}
                style={{
                  ...buttonStyle,
                  width: '100%',
                  height: '48px',
                  marginTop: DS.gap.lg,
                  background: (isSubmitting || !isFormValid) ? 'var(--muted)' : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                  color: 'white',
                  opacity: (isSubmitting || !isFormValid) ? 0.6 : 1,
                  cursor: (isSubmitting || !isFormValid) ? 'not-allowed' : 'pointer',
                }}
              >
                {isSubmitting ? (
                  <><Icons.RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} /> {t('fluxNode.hosting.summary.redirecting')}</>
                ) : (
                  <><Icons.CreditCard size={18} /> {t('fluxNode.hosting.summary.payWithStripe')} ({summaryNodes.length})</>
                )}
              </button>

              {/* Stripe badge */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: DS.gap.xs,
                marginTop: DS.gap.md,
                opacity: 0.6,
              }}>
                <Icons.Lock size={12} />
                <span style={{ fontSize: DS.fontSize.xs }}>Paiement sécurisé par</span>
                <span style={{ fontSize: DS.fontSize.sm, fontWeight: 700, color: '#635bff' }}>Stripe</span>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '30px', color: 'var(--muted)', fontSize: DS.fontSize.sm }}>
              {inputMode === 'manual' ? t('fluxNode.hosting.summary.addNodes') : t('fluxNode.hosting.summary.selectNodes')}
            </div>
          )}
        </div>
      </div>

      {/* ===== MY HOSTINGS SECTION ===== */}
      <div style={{ borderTop: `1px solid ${DS.colors.inputBorder}`, paddingTop: DS.gap.xl }}>
        <div
          onClick={() => setShowManagedNodes(!showManagedNodes)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            marginBottom: showManagedNodes ? DS.gap.lg : 0,
          }}
        >
          <h3 style={{ fontSize: DS.fontSize.xl, fontWeight: 600, display: 'flex', alignItems: 'center', gap: DS.gap.sm, margin: 0 }}>
            <Icons.Server size={20} />
            {t('fluxNode.hosting.myHostedNodes', 'Mes nodes hébergés')}
            {datavexHostedNodes.length > 0 && (
              <span style={{
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                color: 'white',
                padding: '3px 10px',
                borderRadius: '12px',
                fontSize: DS.fontSize.sm,
              }}>
                {datavexHostedNodes.length}
              </span>
            )}
          </h3>
          <Icons.ChevronDown
            size={22}
            style={{
              transition: 'transform 0.2s',
              transform: showManagedNodes ? 'rotate(180deg)' : 'rotate(0deg)',
              color: 'var(--muted)',
            }}
          />
        </div>

        {showManagedNodes && (
          hostedWallets.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: DS.gap.lg }}>
              {hostedWallets.map(walletAddress => {
                const walletHostedNodes = datavexHostedByWallet[walletAddress] || [];
                const walletMonthlyTotal = walletHostedNodes.reduce((sum, { node }) => {
                  const tierLower = node.tier.toLowerCase() as keyof typeof HOSTING_PRICES;
                  return sum + (HOSTING_PRICES[tierLower] || 0);
                }, 0);

                return (
                  <div key={walletAddress} style={{
                    background: DS.colors.sectionBg,
                    borderRadius: DS.radius.lg,
                    border: `1px solid ${DS.colors.inputBorder}`,
                    overflow: 'hidden',
                  }}>
                    {/* Wallet Header */}
                    <div style={{
                      padding: DS.gap.lg,
                      background: 'rgba(0, 0, 0, 0.15)',
                      borderBottom: `1px solid ${DS.colors.inputBorder}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: DS.gap.sm,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: DS.gap.md }}>
                        <Icons.Wallet size={18} style={{ color: '#3b82f6' }} />
                        <div>
                          <div style={{ fontSize: DS.fontSize.sm, fontFamily: 'monospace', fontWeight: 600 }}>
                            {walletAddress.substring(0, 12)}...{walletAddress.substring(walletAddress.length - 8)}
                          </div>
                          <div style={{ fontSize: DS.fontSize.xs, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: DS.gap.sm }}>
                            <span>{walletHostedNodes.length} {walletHostedNodes.length > 1 ? 'nodes' : 'node'}</span>
                            <span style={{
                              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: DS.fontSize.xs,
                              fontWeight: 600,
                            }}>
                              {walletHostedNodes.length} Datavex
                            </span>
                          </div>
                        </div>
                      </div>
                      <div style={{
                        fontSize: DS.fontSize.lg,
                        fontWeight: 700,
                        color: '#3b82f6',
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '4px',
                      }}>
                        {walletMonthlyTotal.toFixed(2)}€
                        <span style={{ fontSize: DS.fontSize.xs, fontWeight: 400, color: 'var(--muted)' }}>/mois</span>
                      </div>
                    </div>

                    {/* Nodes List */}
                    <div style={{ padding: DS.gap.md }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: DS.gap.sm }}>
                        {walletHostedNodes.map(({ node, expiration }) => {
                          const tierLower = node.tier.toLowerCase() as keyof typeof TIER_SPECS;
                          const specs = TIER_SPECS[tierLower];

                          return (
                            <div key={`${node.txhash}:${node.outidx}`} style={{
                              padding: '12px 14px',
                              background: DS.colors.inputBg,
                              borderRadius: DS.radius.md,
                              border: `1px solid ${specs.color}20`,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: DS.gap.sm,
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: DS.gap.md }}>
                                <Icons.Server size={16} style={{ color: specs.color, opacity: 0.7 }} />
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: DS.gap.sm, flexWrap: 'wrap' }}>
                                    <span style={{
                                      background: specs.gradient,
                                      color: 'white',
                                      padding: '3px 8px',
                                      borderRadius: '4px',
                                      fontSize: DS.fontSize.xs,
                                      fontWeight: 700,
                                    }}>
                                      {node.tier.toUpperCase()}
                                    </span>
                                    <span style={{
                                      background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                                      color: 'white',
                                      padding: '3px 8px',
                                      borderRadius: '4px',
                                      fontSize: DS.fontSize.xs,
                                      fontWeight: 600,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '3px',
                                    }}>
                                      <Icons.Server size={9} />
                                      Datavex
                                    </span>
                                    {node.ip && (
                                      <span style={{ fontSize: DS.fontSize.xs, color: 'var(--muted)' }}>{node.ip}</span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: DS.fontSize.xs, fontFamily: 'monospace', color: 'var(--muted)', marginTop: '4px' }}>
                                    {node.txhash.substring(0, 16)}...
                                  </div>
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: DS.gap.md }}>
                                <span style={{
                                  background: expiration.isExpiringSoon ? 'rgba(245, 158, 11, 0.12)' : 'rgba(16, 185, 129, 0.12)',
                                  color: expiration.isExpiringSoon ? '#f59e0b' : '#10b981',
                                  padding: '4px 10px',
                                  borderRadius: DS.radius.sm,
                                  fontSize: DS.fontSize.xs,
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}>
                                  <Icons.Clock size={11} />
                                  {expiration.formatted}
                                </span>
                                <span style={{ fontSize: DS.fontSize.sm, fontWeight: 600, color: specs.color }}>
                                  {HOSTING_PRICES[tierLower]}€
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
              <Icons.Server size={40} style={{ marginBottom: DS.gap.md, opacity: 0.3 }} />
              <p style={{ margin: 0, fontSize: DS.fontSize.md }}>{t('fluxNode.hosting.noHostings', 'Aucun hébergement actif')}</p>
            </div>
          )
        )}
      </div>

      {/* CSS */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        @media (max-width: 900px) {
          .hosting-main-grid { grid-template-columns: 1fr !important; }
          .hosting-summary-panel { order: -1; position: relative !important; top: 0 !important; }
        }
        @media (max-width: 768px) {
          .hosting-header-stats { flex-direction: column !important; align-items: stretch !important; }
          .hosting-stats-row { width: 100%; justify-content: space-between !important; }
          .hosting-stats-row > div { flex: 1; min-width: 0; }
          .hosting-pricing-grid { grid-template-columns: 1fr !important; }
          .hosting-mode-toggle { grid-template-columns: 1fr !important; }
          .hosting-managed-node { flex-direction: column !important; align-items: flex-start !important; }
          .hosting-managed-node-actions { width: 100%; display: flex; justify-content: flex-end; padding-top: 12px; margin-top: 12px; border-top: 1px solid rgba(255,255,255,0.1); }
        }
      `}</style>
    </div>
  );
}
